import type * as sched from 'sched';

export type ConditionResult = 'included' | 'excluded' | 'unknown';

/**
 * Evaluate whether a step's condition includes or excludes it given the
 * current parameter values.
 *
 * - No condition → always 'included'
 * - BoolParameter { key, negate }: check parameterValues[key].
 *   If the key is missing or not a Bool, return 'unknown'.
 */
export function evaluateStepCondition(
  step: sched.WorkflowTemplateStep,
  parameterValues: Record<string, any>,
): ConditionResult {
  const condition = step.condition;
  if (!condition) return 'included';

  if (condition.type === 'BoolParameter') {
    const { key, negate } = condition as { type: 'BoolParameter'; key: string; negate: boolean };
    const val = parameterValues[key];
    if (val == null) return 'unknown';
    // ParameterValue::Bool serde: { type: "Bool", value: true/false }
    const boolVal = val?.type === 'Bool' ? val.value : undefined;
    if (boolVal == null) return 'unknown';
    const effective = negate ? !boolVal : boolVal;
    return effective ? 'included' : 'excluded';
  }

  // Unknown condition type — treat as unknown
  return 'unknown';
}

/**
 * Compute a set of step IDs that are "configured out" (excluded) by conditions.
 */
export function getExcludedStepIds(
  templateState: sched.WorkflowTemplateState,
  parameterValues: Record<string, any>,
): Set<string> {
  const excluded = new Set<string>();
  for (const step of templateState.steps) {
    if (evaluateStepCondition(step, parameterValues) === 'excluded') {
      excluded.add(step.id);
    }
  }
  return excluded;
}
